import React, { useState } from 'react';
import { sendWorkersCommand } from '../api';

/**
 * Перезапуск агента, перезавантаження й вимкнення машин з /cluster (агент 70+).
 *
 * 🚨 ДОЗВІЛ ДАЄ ЛЮДИНА ЗА МАШИНОЮ, НЕ АДМІН. Галочки — у вікні агента
 * (127.0.0.1:8787, «Віддалене керування»); сюди вони лише приходять у звіті.
 * Сервер перевіряє їх ще раз, і сам агент — утретє: кнопка, що «пропустила»
 * машину без дозволу, нічого не зробить.
 *
 * Перезапуск агента — під дозволом «керувати налаштуваннями» (він
 * увімкнений типово, як і правки з адмінки з випуску 69); перезавантаження й
 * вимкнення — під окремим дозволом, типово вимкненим.
 */
const ACTIONS = [
  { key: 'restart_agent', label: 'Перезапустити агента', need: 'settings',
    confirm: 'Перезапустити агента? Блок, який машина зараз рахує, повернеться в чергу.' },
  { key: 'reboot', label: 'Перезавантажити машину', need: 'power',
    confirm: 'Перезавантажити машину? Windows покаже попередження і перезавантажиться за хвилину.' },
  { key: 'shutdown', label: 'Вимкнути машину', need: 'power', danger: true,
    confirm: 'Вимкнути машину? Увімкнути її звідси назад НЕ можна — лише людина на місці.' },
];

/** Причина, з якої на цій машині дія недоступна, або ''. Дзеркало
 *  node_settings.can_command — сервер усе одно перевірить сам. */
export function commandBlock(w, need) {
  const st = w.settings || {};
  if (!st.commands_supported) return 'агент старший за випуск 70';
  const rem = st.remote || {};
  if (!rem.known) return 'машина ще не повідомила дозволи';
  if (need === 'settings' && !rem.settings) return 'не дозволено на машині';
  if (need === 'power') {
    if (!rem.power) return 'не дозволено на машині';
    if (!st.power_supported) return 'агент не керує живленням цієї машини';
  }
  return '';
}

export default function NodeCommandModal({ targets, onClose, onSent }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const many = targets.length > 1;

  const run = async (a) => {
    const ok = targets.filter(w => !commandBlock(w, a.need));
    if (!ok.length) return;
    if (!window.confirm(`${a.confirm}\n\nМашин: ${ok.length} (${ok.map(w => w.name).join(', ')})`)) return;
    setBusy(a.key); setErr(''); setResult(null);
    try {
      const res = await sendWorkersCommand(ok.map(w => w.agent_id), a.key);
      setResult({ action: a.label, ...res.data });
      onSent?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'не надіслано');
    } finally {
      setBusy('');
    }
  };

  return (
    <div style={S.back} onClick={onClose}>
      <div style={S.box} onClick={e => e.stopPropagation()}>
        <div style={S.head}>
          <b>Керування {many ? `${targets.length} машинами` : `«${targets[0].name}»`}</b>
          <button style={S.x} onClick={onClose}>✕</button>
        </div>
        <div style={S.note}>
          Діє лише там, де людина за машиною дозволила це у вікні агента
          (127.0.0.1:8787 → «Віддалене керування»). Команда чекає машину
          10 хвилин: вимкнена машина, прокинувшись пізніше, її не виконає.
        </div>

        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Машина</th>
              <th style={S.th}>Агент</th>
              <th style={S.th} title="Перезавантаження й вимкнення машини">Живлення</th>
            </tr>
          </thead>
          <tbody>
            {targets.map(w => {
              const a = commandBlock(w, 'settings');
              const p = commandBlock(w, 'power');
              return (
                <tr key={w.agent_id}>
                  <td style={S.td}>{w.name}</td>
                  <td style={{ ...S.td, color: a ? '#B45309' : '#007A41' }}>{a || 'можна'}</td>
                  <td style={{ ...S.td, color: p ? '#B45309' : '#007A41' }}>{p || 'можна'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={S.btns}>
          {ACTIONS.map(a => {
            const n = targets.filter(w => !commandBlock(w, a.need)).length;
            return (
              <button key={a.key} disabled={!n || !!busy} onClick={() => run(a)}
                      title={n ? '' : 'Жодній із вибраних машин це не дозволено'}
                      style={{ ...S.btn, ...(a.danger ? S.danger : S.on),
                               ...(!n ? S.off : null) }}>
                {busy === a.key ? 'Надсилаю…' : a.label}{many && n ? ` (${n})` : ''}
              </button>
            );
          })}
        </div>

        {result && (
          <div style={S.ok}>
            {result.action}: надіслано {result.sent?.length || 0}
            {result.refused?.length
              ? `; відмовлено — ${result.refused.map(x => `${x.name || x.agent_id} (${x.reason})`).join(', ')}`
              : ''}.
            {' '}Машина візьме команду на найближчому стуку (до 20 с).
          </div>
        )}
        {err && <div style={S.warn}>{err}</div>}
      </div>
    </div>
  );
}

const S = {
  back: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 1000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          padding: '40px 12px', overflowY: 'auto' },
  box:  { background: '#fff', borderRadius: 10, padding: '16px 18px', width: 'min(640px, 100%)',
          boxShadow: '0 12px 32px rgba(15,23,42,0.2)', fontSize: 13.5, color: '#1A1A1A' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  x:    { border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#5B646F' },
  note: { color: '#5B646F', fontSize: 12.5, lineHeight: 1.5, marginBottom: 10 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginBottom: 12 },
  th:   { textAlign: 'left', padding: '6px 6px', borderBottom: '1px solid #E5E7EB',
          color: '#5B646F', fontWeight: 600 },
  td:   { padding: '5px 6px', borderBottom: '1px solid #F1F3F5' },
  btns: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  btn:  { padding: '7px 12px', borderRadius: 6, border: '1px solid #E5E7EB',
          fontSize: 13, cursor: 'pointer' },
  on:   { background: '#475FFA', color: '#fff', borderColor: '#475FFA' },
  danger: { background: '#FFFFFF', color: '#C02B5E', borderColor: '#F5C2D3' },
  off:  { opacity: 0.45, cursor: 'not-allowed' },
  ok:   { marginTop: 12, color: '#166534', fontSize: 13, lineHeight: 1.5 },
  warn: { marginTop: 12, padding: '8px 10px', background: '#FEF3C7', color: '#92400E',
          borderRadius: 6, fontSize: 13 },
};
